import { useEffect, useMemo, useState, useRef, forwardRef } from 'react';
import {
  Group,
  Text,
  Divider,
  Button,
  Badge,
  Paper,
  Flex,
  Box,
  Select,
  useMantineTheme,
} from '@mantine/core';
import { IconHelp, IconAlertTriangle, IconInfoCircle, IconBulb, IconChevronDown } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import ReactDOM from 'react-dom';
import { SelectTooltip, VisiLoader } from "../../GeneralComponent/GeneralComponent";
import { useNavigate } from 'react-router-dom';
import SettingsApi from '../../../api/SettingsApi';

type ResetGoalOption = {
  key: string;
  name: string;
  type: 'webhook' | 'postback' | 'goal';
  trackedIn: string[];
};

const HOW_RECORDED: Record<ResetGoalOption['type'], string> = {
  webhook:  'This goal is recorded automatically through a connected webhook.',
  postback: 'This goal is recorded automatically through a connected postback.',
  goal:     'This goal is recorded when the customer reaches a specific page.',
};

// Goal order the reset logic uses: webhooks, then goal pages, then postbacks.
const GROUP_ORDER: ResetGoalOption['type'][] = ['webhook', 'goal', 'postback'];
const GROUP_LABEL: Record<ResetGoalOption['type'], string> = {
  webhook:  'Webhooks',
  goal:     'Goal Pages',
  postback: 'Postbacks',
};
const NONE_VALUE = 'none';
const NONE_LABEL = 'None — journeys never reset';
// One goal can be tracked in a dozen campaigns; the row shows a couple and counts the rest.
const TRACKED_PREVIEW = 2;

const trackedLine = (names: string[]) => {
  if (names.length === 0) { return 'Not tracked in any campaign yet'; }
  const shown = names.slice(0, TRACKED_PREVIEW).join(', ');
  const rest = names.length - TRACKED_PREVIEW;
  return rest > 0 ? 'Tracked in ' + shown + ' +' + rest + ' more' : 'Tracked in ' + shown;
};

type PickerItemProps = React.ComponentPropsWithoutRef<'div'> & {
  label: string;
  tracked: string;
  badge: boolean;
  value: string;
  group?: string;
  goalType?: string;
  trackedIn?: string[];
};

// Two-line row: goal name, with the campaigns that track it underneath.
const PickerItem = forwardRef<HTMLDivElement, PickerItemProps>(
  ({ label, tracked, badge, value, group, goalType, trackedIn, ...others }, ref) => (
    <div ref={ref} {...others}>
      <Flex align="center" justify="space-between" gap="sm" wrap="nowrap">
        <Box sx={{ minWidth: 0 }}>
          <Text size="sm" weight={600} truncate>{label}</Text>
          {tracked !== '' && (
            <Text size="xs" color="dimmed" truncate>{tracked}</Text>
          )}
        </Box>
        {badge && (
          <Badge size="sm" variant="outline" color="blue" sx={{ flex: '0 0 auto' }}>
            Current setting
          </Badge>
        )}
      </Flex>
    </div>
  )
);

// active: the tab panel stays mounted, so opening the tab again re-reads the saved reset goal.
export function AttributionSettings({ SelectMemberWebsiteListValue, active = true }: any) {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const isDark = theme.colorScheme === 'dark';
  const [resetGoal, setResetGoal] = useState(NONE_VALUE);
  // What the site is saved on right now; drives the "Current setting" badge.
  const [savedGoal, setSavedGoal] = useState(NONE_VALUE);
  const [goals, setGoals] = useState<ResetGoalOption[]>([]);
  const [loading, setLoading] = useState(false);
  const selected = goals.find((g) => g.key === resetGoal) ?? null;

  useEffect(() => {
    if (!active) { return; }
    if (!SelectMemberWebsiteListValue) { setGoals([]); return; }
    setLoading(true);
    SettingsApi
      .getattributionresetgoals({ site_id: SelectMemberWebsiteListValue })
      .then((response: any) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) { navigate('/logout'); }
        return null;
      })
      .then((data: any) => {
        const list = data && data.status === 1 && Array.isArray(data.data?.goals) ? data.data.goals : [];
        setGoals(list
          .map((g: any) => ({
            key: String(g.key ?? ''),
            name: String(g.name ?? ''),
            type: (g.type === 'webhook' || g.type === 'postback') ? g.type : 'goal',
            trackedIn: Array.isArray(g.tracked_in) ? g.tracked_in : [],
          }))
          .filter((g: ResetGoalOption) => g.key !== ''));
        // Open on whatever is saved; the endpoint already blanks a goal that no longer exists.
        const saved = String(data?.data?.selected_goal_key ?? '');
        const resolved = saved !== '' && list.some((g: any) => g.key === saved) ? saved : NONE_VALUE;
        setResetGoal(resolved);
        setSavedGoal(resolved);
      })
      .catch((e: any) => console.log(e))
      .finally(() => setLoading(false));
  }, [SelectMemberWebsiteListValue, active]);

  const attributionRef = useRef(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpTargetId, setHelpTargetId] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({});

  const [saving, setSaving] = useState(false);
  const save = () => {
    if (!SelectMemberWebsiteListValue) { return; }
    setSaving(true);
    SettingsApi
      .saveattributionresetgoal({
        site_id: SelectMemberWebsiteListValue,
        goal_key: resetGoal === NONE_VALUE ? '' : resetGoal,
      })
      .then((response: any) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) { navigate('/logout'); }
        return null;
      })
      .then((data: any) => {
        if (data && data.status === 1) {
          setSavedGoal(resetGoal);
          notifications.show({ title: 'Saved', message: data.message, color: 'teal' });
        } else {
          notifications.show({
            title: 'Error',
            message: data?.message ?? 'Could not save the reset goal.',
            color: 'red',
          });
        }
      })
      .catch((e: any) => console.log(e))
      .finally(() => setSaving(false));
  };

  const handleHelpClick = (id:any) => {
    setShowHelpModal(true);
    setHelpTargetId(id);
    if (attributionRef.current) {
      const { top, left, width } = attributionRef.current.getBoundingClientRect();
      const scrollOffset = window.scrollY;
      setTooltipPosition({
        [id]: {top: top + scrollOffset, left: left + width + 10 },
      });
    }
  };

  // Grouped picker rows: None first, then webhooks, goal pages, postbacks.
  const pickerData = useMemo(() => {
    const items: any[] = [{
      value: NONE_VALUE,
      label: NONE_LABEL,
      group: 'Recommended',
      tracked: '',
      trackedIn: [],
      goalType: 'none',
      badge: savedGoal === NONE_VALUE,
    }];
    GROUP_ORDER.forEach((type) => {
      goals.filter((g) => g.type === type).forEach((g) => items.push({
        value: g.key,
        label: g.name,
        group: GROUP_LABEL[type],
        tracked: trackedLine(g.trackedIn),
        trackedIn: g.trackedIn,
        goalType: type,
        badge: savedGoal === g.key,
      }));
    });
    return items;
  }, [goals, savedGoal]);

  // Tinted shell: teal for the tip, blue for the explanatory notes.
  const callout = (tone: 'teal' | 'blue', icon: React.ReactNode, children: React.ReactNode) => (
    <Paper
      withBorder
      radius="md"
      p="sm"
      mb="sm"
      sx={{
        borderColor: theme.colors[tone][isDark ? 8 : 2],
        backgroundColor: isDark ? theme.fn.rgba(theme.colors[tone][9], 0.15) : theme.colors[tone][0],
      }}
    >
      <Flex gap="sm" align="flex-start">
        <Box sx={{ flex: '0 0 auto', marginTop: 2 }}>{icon}</Box>
        <Text size="sm" sx={{ lineHeight: 1.55 }}>{children}</Text>
      </Flex>
    </Paper>
  );

  const mutedIcon = isDark ? theme.colors.dark[1] : theme.colors.gray[6];

  return (
    <>
      <VisiLoader VisiLoaderVisible={loading} />
      {showHelpModal &&
        ReactDOM.createPortal(
          <div>
            {Object.keys(tooltipPosition).map((key) => (
              <SelectTooltip
                isVisible={showHelpModal}
                setIsVisible={setShowHelpModal}
                id={helpTargetId}
                tooltipPosition={tooltipPosition[key]} 
              />
            ))}
          </div>,
        document.body
      )}
      <Group position="apart" mb="xs">
        <Text weight={500}>ATTRIBUTION <IconHelp ref={attributionRef} size="1.1rem" color="red" style={{marginBottom:-3, cursor: 'pointer'}} onClick={() => handleHelpClick(386)} /></Text>
      </Group>

      <Text size="xl" weight={700} mb={8}>When does a customer's journey start over?</Text>
      <Text size="sm" mb="md" maw={860} sx={{ lineHeight: 1.6 }}>
        Pick the goal that marks the end of a customer's journey — usually your main conversion, like a
        booked call or a purchase. Once a customer reaches it, their next visit starts a fresh journey.
        Everything they did on the way to that goal still counts toward it; only what comes afterward
        starts over.
      </Text>

      {callout('teal', <IconBulb size="1.15rem" color={theme.colors.teal[isDark ? 4 : 7]} />, (
        <>
          <b>Not sure which to choose?</b> Most businesses pick their <b>main conversion</b> — the final
          step a customer takes, such as a purchase or a booked call. Leave it on <b>None</b> if your
          customers usually convert once and don't come back to start a new journey. You can change this
          anytime.
        </>
      ))}

      {callout('blue', <IconInfoCircle size="1.15rem" color={theme.colors.blue[isDark ? 4 : 7]} />, (
        <>
          <b>Where this applies:</b> this controls your <b>combined reports</b> — the across-campaign view
          in your main reporting, where one customer's journey can cross more than one funnel. When you open
          a <b>single funnel on its own</b>, that funnel still resets at its own finish line, the way it does
          today.
        </>
      ))}

      <Text size="sm" weight={500} mt="lg" mb="xs">
        Reset Goal
      </Text>

      <Select
        value={resetGoal}
        onChange={(v) => setResetGoal(v || NONE_VALUE)}
        data={pickerData}
        itemComponent={PickerItem}
        searchable
        allowDeselect={false}
        nothingFound="No goals found"
        maxDropdownHeight={320}
        withinPortal
        disabled={loading}
        maw={620}
        styles={{
          input: { height: 52, fontWeight: 500 },
          rightSection: { pointerEvents: 'none' },
        }}
        // Search matches the goal name and the campaigns it is tracked in.
        filter={(query: string, item: any) => {
          const needle = query.trim().toLowerCase();
          if (needle === '') { return true; }
          const hay = (String(item.label ?? '') + ' ' + (Array.isArray(item.trackedIn) ? item.trackedIn.join(' ') : '')).toLowerCase();
          return hay.indexOf(needle) !== -1;
        }}
        rightSection={(
          <Flex align="center" gap={8} sx={{ whiteSpace: 'nowrap' }}>
            {resetGoal === savedGoal && (
              <Badge size="sm" variant="outline" color="blue">
                Current setting
              </Badge>
            )}
            <IconChevronDown size="1rem" color={mutedIcon} />
          </Flex>
        )}
        rightSectionWidth={resetGoal === savedGoal ? 170 : 34}
      />
      {!loading && goals.length === 0 && (
        <Text size="xs" color="dimmed" mt={6}>
          No goals are configured for this website yet.
        </Text>
      )}

      {/* Site-wide caution sits with the picker; wording escalates once a real goal is chosen. */}
      <Paper
        withBorder
        radius="md"
        p="sm"
        mt="sm"
        maw={900}
        sx={{
          borderColor: theme.colors.orange[isDark ? 8 : 3],
          backgroundColor: isDark ? theme.fn.rgba(theme.colors.orange[9], 0.15) : theme.colors.orange[0],
        }}
      >
        <Flex gap="sm" align="flex-start">
          <IconAlertTriangle
            size="1.15rem"
            color={theme.colors.orange[isDark ? 4 : 7]}
            style={{ flex: '0 0 auto', marginTop: 2 }}
          />
          <Text size="sm" sx={{ lineHeight: 1.55 }}>
            {selected ? (
              <>
                <b>Every campaign and funnel on this site will reset at {selected.name}.</b> This is a
                site-wide change to your combined reporting, not a change to one campaign. If you're not
                sure, switch back to <b>None — no reset</b>.
              </>
            ) : (
              <>
                <b>This setting is site-wide.</b> Changing it affects the combined reporting for{' '}
                <b>every</b> campaign and funnel on this site, not just one. If you're not sure, leave it
                on <b>None — no reset</b>.
              </>
            )}
          </Text>
        </Flex>
      </Paper>

      {/* Full campaign list + plain-language example for the chosen goal. */}
      <Paper
        withBorder
        radius="md"
        p="sm"
        mt="sm"
        maw={900}
        sx={{
          borderColor: isDark ? theme.colors.dark[4] : theme.colors.gray[3],
          backgroundColor: isDark ? theme.fn.rgba(theme.colors.dark[6], 0.6) : theme.colors.gray[0],
        }}
      >
        {selected ? (
          <>
            <Text size="sm" weight={500}>
              Tracked In
            </Text>
            {selected.trackedIn.length > 0 ? (
              <Flex gap={6} wrap="wrap" mt={6}>
                {selected.trackedIn.map((c) => (
                  <Badge
                    key={c}
                    size="sm"
                    variant="outline"
                    color="gray"
                    radius="sm"
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                  >
                    {c}
                  </Badge>
                ))}
              </Flex>
            ) : (
              <Text size="sm" color="dimmed" mt={6}>Not tracked in any campaign yet.</Text>
            )}
            <Text size="sm" mt="sm" sx={{ lineHeight: 1.55 }}>
              With this setting, once a customer reaches <b>{selected.name}</b>, their journey is complete.
              If they come back later and convert again, that starts a <b>brand-new journey</b> — so your
              reports won't credit the same earlier ads twice for a repeat {selected.name.toLowerCase()}.
            </Text>
            <Text size="xs" color="dimmed" mt={6}>{HOW_RECORDED[selected.type]}</Text>
          </>
        ) : (
          <Text size="sm" sx={{ lineHeight: 1.55 }}>
            A customer's journey runs from their first visit through every conversion that follows. Nothing
            resets it — this is the safest choice if you're not sure.
          </Text>
        )}
      </Paper>

      <Paper
        withBorder
        radius="md"
        p="sm"
        mt="sm"
        maw={900}
        sx={{
          borderColor: theme.colors.blue[isDark ? 8 : 2],
          backgroundColor: isDark ? theme.fn.rgba(theme.colors.blue[9], 0.15) : theme.colors.blue[0],
        }}
      >
        <Flex gap="sm" align="flex-start">
          <IconInfoCircle
            size="1.15rem"
            color={theme.colors.blue[isDark ? 4 : 7]}
            style={{ flex: '0 0 auto', marginTop: 2 }}
          />
          <Text size="sm" sx={{ lineHeight: 1.55 }}>
            <b>Takes effect for conversions from now on.</b> Attribution is worked out the moment a
            conversion happens, so conversions already recorded keep the credit they were given. Reports
            will use the new reset goal only for conversions that happen after you save — past data stays
            exactly as it is.
          </Text>
        </Flex>
      </Paper>

      <Divider my="md" />

      <Group mb={100} spacing="md">
        <Button type="button" onClick={save} loading={saving} disabled={loading}>
          Save Settings
        </Button>
        <Text size="sm" color="dimmed">Applies to this website. You can change it anytime.</Text>
      </Group>
    </>
  );
}
